import { useEffect, useState } from 'react';
import {
  BACKGROUND_PRIORITY, applySavedTemplate, applyTemplate, savedTemplateFits, setElementProp, toSavedTemplate, rgb, type ProjectDoc, type Element, type SavedTemplate,
} from '@cal/engine';
import { SavedTemplatePreview } from './Wireframe';
import { get, post, errorMessage } from '../api';
import { Check, ColorField, Confirm, Field, FontField, Modal, Select } from '../ui/controls';
import { toast } from '../ui/toast';
import { edit, useEditor } from './store';

interface Tpl { id: string; name: string; description: string; product?: 'monthly' | 'year' | 'tent'; audience?: string }
const AUDIENCE: Record<string, string> = { business: 'Business', personal: 'Personal', 'estate-agent': 'Estate agent', custom: 'Blank' };
interface CustomTpl { id: string; name: string; description: string; data: SavedTemplate }

export function DesignStage({ doc }: { doc: ProjectDoc }) {
  const [templates, setTemplates] = useState<{ builtin: Tpl[]; custom: CustomTpl[] }>({ builtin: [], custom: [] });
  const myTemplates = templates.custom.filter((t) => savedTemplateFits(t.data, doc));
  const [pending, setPending] = useState<Tpl | CustomTpl | null>(null);
  const [saveTpl, setSaveTpl] = useState(false);
  const reload = () => get<{ builtin: Tpl[]; custom: CustomTpl[] }>('/api/templates').then(setTemplates).catch(() => undefined);
  useEffect(() => { void reload(); }, []);

  const applyAll = (fn: (el: Element) => [string[], unknown][] | null) =>
    edit((d) => {
      let out = d;
      for (const design of ['month', 'cover', 'backing', 'year', 'face', 'carrier'] as const)
        for (const el of out.designs[design].elements) for (const [path, v] of fn(el) ?? []) out = setElementProp(out, 'all', design, el.id, path, v);
      return out;
    }, 'quick-style');

  // year planners and tent cards both use the year-grid styles
  const isYear = doc.product.productType === 'year' || doc.product.productType === 'tent';
  const yearDesign = doc.product.productType === 'tent' ? doc.designs.face : doc.designs.year;
  const main = isYear ? yearDesign : doc.designs.month;
  const headingFont = (main.elements.find((e) => e.id === 'heading') as Extract<Element, { type: 'text' }> | undefined)?.style.fontId ?? 'montserrat-700';
  const grid = doc.designs.month.elements.find((e) => e.type === 'grid') as Extract<Element, { type: 'grid' }> | undefined;
  const yearEl = yearDesign.elements.find((e) => e.type === 'yeargrid') as Extract<Element, { type: 'yeargrid' }> | undefined;
  const accent = (isYear ? yearEl?.style.titleFill : grid?.style.header.fill) ?? null;

  return (
    <div>
      <div className="section col">
        <h2>2 · Design</h2>
        <h3>Templates</h3>
        <div className="tpl-grid">
          {templates.builtin.filter((t) => (t.product ?? 'monthly') === doc.product.productType).map((t) => (
            <button key={t.id} className="tpl-card" aria-pressed={doc.templateId === t.id} onClick={() => setPending(t)} title={t.description}>
              <TemplateIcon id={t.id} />
              <span className="small" style={{ fontWeight: 600 }}>{t.name}</span>
              {t.audience && t.audience !== 'general' && <span className="badge">{AUDIENCE[t.audience]}</span>}
            </button>
          ))}
        </div>
        {myTemplates.length > 0 && <><h3>My templates</h3>
        <div className="tpl-grid">
          {myTemplates.map((t) => (
            <button key={t.id} className="tpl-card" onClick={() => setPending(t)} title={t.description}>
              {t.data.product ? <SavedTemplatePreview t={t.data} width={60} /> : <TemplateIcon id={t.data.baseTemplateId} />}
              <span className="small" style={{ fontWeight: 600 }}>{t.name}</span>
            </button>
          ))}
        </div></>}
        <button className="btn sm" onClick={() => setSaveTpl(true)}>Save this design as a template</button>
        <p className="small faint" style={{ margin: 0 }}>Your templates are offered for projects of the same calendar type, size and orientation ({doc.product.widthMm} × {doc.product.heightMm} mm).</p>
      </div>
      <div className="section col">
        <h3>Calendar layout</h3>
        <div className="grid2">
          <Select label="Week starts on" value={doc.calendar.weekStart} onChange={(v) => edit((d) => ({ ...d, calendar: { ...d.calendar, weekStart: v } }), 'cal')} options={[{ value: 'monday', label: 'Monday' }, { value: 'sunday', label: 'Sunday' }]} />
          {doc.product.productType === 'monthly' && <Select label="Week rows" value={doc.calendar.rows} onChange={(v) => edit((d) => ({ ...d, calendar: { ...d.calendar, rows: v } }), 'cal')} options={[{ value: 'actual', label: 'As needed (4–6)' }, { value: 'fixed6', label: 'Always 6' }]} />}
        </div>
        {doc.product.productType === 'monthly' && <Select label="Days from previous/next month" value={doc.calendar.adjacent} onChange={(v) => edit((d) => ({ ...d, calendar: { ...d.calendar, adjacent: v } }), 'cal')} options={[{ value: 'blank', label: 'Leave blank' }, { value: 'show', label: 'Show dates (lighter style)' }]} />}
        {doc.product.productType === 'monthly' && <Check label="Show ISO week numbers" checked={doc.calendar.weekNumbers} onChange={(v) => edit((d) => ({ ...d, calendar: { ...d.calendar, weekNumbers: v } }), 'cal')} />}
        <p className="small faint">ISO 8601 weeks: weeks start Monday; week 1 contains the year's first Thursday. Each row shows the week number of its Monday.</p>
      </div>
      <div className="section col">
        <h3>Quick styles (all pages)</h3>
        <FontField label="Heading font" value={headingFont} onChange={(id) => applyAll((el) => (el.type === 'text' && (el.role === 'heading' || el.role === 'subheading') ? [[['style', 'fontId'], id]] : el.type === 'mini' ? [[['title', 'fontId'], id]] : el.type === 'yeargrid' ? [[['style', 'title', 'fontId'], id]] : null))} />
        {isYear && yearEl && <FontField label="Calendar font" value={yearEl.style.date.fontId} onChange={(id) => applyAll((el) => (el.type === 'yeargrid' ? [[['style', 'date', 'fontId'], id], [['style', 'weekday', 'fontId'], id]] : null))} />}
        <ColorField label="Accent colour" value={accent} allowNone brandColors={doc.brand.colors} onChange={(c) => applyAll((el) => {
          if (el.type === 'grid') return [[['style', 'header', 'fill'], c]];
          if (el.type === 'yeargrid') return [[['style', 'titleFill'], c]];
          if (el.type === 'shape' && el.id === 'band' && el.fill && (el.fill.space !== 'rgb' || el.fill.hex !== '#eef1f5')) return [[['fill'], c]];
          if (el.type === 'text' && el.id === 'phone' && el.panel) return [[['panel', 'fill'], c]];
          return null;
        })} />
        {!isYear && grid && <FontField label="Calendar font" value={grid.style.dateText.fontId} onChange={(id) => applyAll((el) => (el.type === 'grid' ? [[['style', 'dateText', 'fontId'], id], [['style', 'adjacentText', 'fontId'], id]] : null))} />}
        {!isYear && grid && (
          <div className="grid2">
            <ColorField label="Weekend shading" value={grid.style.saturday.fill} allowNone brandColors={doc.brand.colors} onChange={(c) => applyAll((el) => (el.type === 'grid' ? [[['style', 'saturday', 'fill'], c], [['style', 'sunday', 'fill'], c]] : null))} />
          </div>
        )}
        {isYear && yearEl && (
          <div className="grid2">
            <ColorField label="Weekend dates" value={yearEl.style.sundayColor} allowNone brandColors={doc.brand.colors} onChange={(c) => applyAll((el) => (el.type === 'yeargrid' ? [[['style', 'saturdayColor'], c], [['style', 'sundayColor'], c]] : null))} />
            <ColorField label="Weekend shading" value={yearEl.style.weekendFill} allowNone brandColors={doc.brand.colors} onChange={(c) => applyAll((el) => (el.type === 'yeargrid' ? [[['style', 'weekendFill'], c]] : null))} />
          </div>
        )}
        <p className="small faint">Select the calendar on the page for full control of every text style, colour and spacing.</p>
      </div>
      <div className="section col">
        <h3>Logo, contact details & colours</h3>
        <p className="small muted" style={{ margin: 0 }}>These are filled in under Content → Brand, with the photographs and holidays.</p>
        <button className="btn sm" onClick={() => useEditor.setState({ stage: 3, contentTab: 'brand' })}>Open Content → Brand</button>
      </div>
      <div className="section">
        <h3>Day background priority</h3>
        <p className="small muted">When several apply to one day, the highest wins. All event labels are still shown.</p>
        <ol className="priority-list">{BACKGROUND_PRIORITY.map((p) => <li key={p.tier}>{p.label}</li>)}</ol>
      </div>
      {pending && (
        <Confirm title={`Use “${pending.name}”?`} confirmLabel="Apply template" onClose={() => setPending(null)}
          body={<p>The page layout, styles and calendar area will be replaced, and monthly overrides cleared. Your photographs, captions, holidays and branding are kept. You can undo this.</p>}
          onConfirm={() => {
            if ('data' in pending) {
              const t = pending.data;
              edit((d) => applySavedTemplate(d, t), 'template');
            } else edit((d) => applyTemplate(d, pending.id), 'template');
            useEditor.setState({ selected: null });
          }} />
      )}
      {saveTpl && <SaveTemplate doc={doc} onClose={() => { setSaveTpl(false); void reload(); }} />}
    </div>
  );
}

function SaveTemplate({ doc, onClose }: { doc: ProjectDoc; onClose: () => void }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const t = toSavedTemplate(doc);
  return (
    <Modal title="Save design as template" onClose={onClose} footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn primary" disabled={!name.trim()} onClick={async () => {
      try {
        await post('/api/templates', { name, description: description.trim() || `${doc.product.widthMm} × ${doc.product.heightMm} mm`, data: toSavedTemplate(doc) });
        toast('Template saved', 'ok'); onClose();
      } catch (e) { toast(errorMessage(e), 'err'); }
    }}>Save template</button></>}>
      <div className="row" style={{ alignItems: 'flex-start', gap: 12 }}>
        <SavedTemplatePreview t={t} width={110} />
        <div className="col grow">
          <Field label="Template name"><input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Corporate navy" /></Field>
          <Field label="Description (optional)"><input className="input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. Photo top, logo bottom right" /></Field>
        </div>
      </div>
      <p className="small muted">Saves the page size, orientation, calendar type, clearance, layout grid, calendar position and style, and every block with its size and position. Photos, the logo file, events and monthly overrides are not included. The template is offered for this size and orientation only.</p>
    </Modal>
  );
}

/** Tiny schematic of each starter template. */
export function TemplateIcon({ id }: { id: string }) {
  const g = '#c7cdd4', p = '#8fa3b8', d = '#1f2a37';
  const grid = (y: number, h: number) => <g>{[0, 1, 2, 3, 4].map((r) => <rect key={r} x={6} y={y + (r * h) / 5} width={48} height={h / 5 - 1} fill="none" stroke={g} strokeWidth={0.6} />)}<rect x={6} y={y - 3} width={48} height={3} fill={d} /></g>;
  return (
    <div className="tpl-prev">
      <svg width="60" height="84" viewBox="0 0 60 84" aria-hidden>
        <rect width="60" height="84" fill="#fff" />
        {id === 'photo-top' && <><rect x={6} y={8} width={48} height={34} fill={p} />{grid(52, 26)}</>}
        {id === 'full-grid' && <><rect x={6} y={8} width={28} height={6} fill={d} /><rect x={40} y={8} width={6} height={6} fill={g} /><rect x={48} y={8} width={6} height={6} fill={g} />{grid(22, 54)}</>}
        {id === 'collage' && <><rect x={6} y={8} width={29} height={34} fill={p} /><rect x={37} y={8} width={17} height={16} fill={p} /><rect x={37} y={26} width={17} height={16} fill={p} />{grid(52, 26)}</>}
        {id === 'background-photo' && <><rect width={60} height={84} fill={p} /><rect x={4} y={40} width={52} height={40} rx={2} fill="#fff" opacity={0.9} />{grid(50, 26)}</>}
        {id === 'business' && <><rect x={6} y={8} width={48} height={30} fill={p} />{grid(46, 24)}<rect x={0} y={74} width={60} height={10} fill="#e3e7ec" /><rect x={6} y={77} width={12} height={4} fill={d} /></>}
        {id === 'estate-agent' && <><rect x={6} y={8} width={48} height={30} fill={p} />{grid(46, 22)}<rect x={0} y={72} width={60} height={12} fill={d} /><circle cx={11} cy={78} r={4} fill={p} /><rect x={44} y={74} width={12} height={8} fill="#fff" /></>}
        {id.startsWith('year-') && (
          <g>
            {id === 'year-photo' && <rect x={0} y={0} width={60} height={18} fill={p} />}
            {id === 'year-personal' && <rect x={6} y={5} width={48} height={24} rx={2} fill={p} />}
            {id === 'year-estate-agent' && <><rect x={0} y={0} width={60} height={14} fill={d} /><circle cx={9} cy={7} r={4} fill={p} /></>}
            {id === 'year-business' && <><rect x={20} y={4} width={20} height={6} fill={d} /><rect x={12} y={12} width={36} height={4} rx={2} fill={d} /></>}
            {id === 'year-poster' && <><rect x={6} y={4} width={12} height={6} fill={d} /><rect x={24} y={4} width={14} height={6} fill={d} /></>}
            {Array.from({ length: 12 }, (_, i) => <rect key={i} x={6 + (i % 3) * 16.7} y={(id === 'year-personal' ? 34 : 22) + Math.floor(i / 3) * (id === 'year-personal' ? 12 : 14)} width={14} height={id === 'year-personal' ? 10 : 12} fill="none" stroke={g} strokeWidth={0.8} />)}
            {Array.from({ length: 12 }, (_, i) => <rect key={`t${i}`} x={6 + (i % 3) * 16.7} y={(id === 'year-personal' ? 34 : 22) + Math.floor(i / 3) * (id === 'year-personal' ? 12 : 14)} width={14} height={2.2} rx={1} fill={d} />)}
          </g>
        )}
      </svg>
    </div>
  );
}
